import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface RunnerPairingConfirmation {
  runnerName: string;
}

/**
 * Ein verbundener Rechner, wie ihn die Kontoseite und die Auswahl im Start-Dialog zeigen.
 */
export interface PairedRunner {
  id: string;
  name: string;
  lastSeenAt: string;
  /** Momentaufnahme: Ein Rechner bleibt verbunden, auch wenn er gerade aus ist. */
  online: boolean;
  preferred: boolean;
  /**
   * Ob dieser Mensch den Rechner trennen darf. Kommt vom Server, damit die Regel — Einrichter,
   * eigener Rechner oder Verwalter — nicht ein zweites Mal hier nachgebaut wird.
   */
  canDisconnect: boolean;
  /** Vom Rechner selbst gemeldet; hier nur lesbar. */
  reportedLabels: string[];
  /** In testOFFICE gepflegt; der Herzschlag des Rechners fasst sie nicht an. */
  managedLabels: string[];
  /** Ob dieser Mensch die gepflegten Etiketten aendern darf — dieselbe Regel wie beim Trennen. */
  canEditLabels: boolean;
  engine: string;
  version: string | null;
  /** Ob der Rechner diesem Menschen gehört; die Kontoseite zeigt nur diese (NEX-1729). */
  mine: boolean;
  currentJob: RunnerCurrentJob | null;
  queuedCount: number;
  /** Getrennte Rechner liefert der Server nur mit `includeDisconnected` (NEX-1734). */
  connected: boolean;
  canRename: boolean;
  /** Nur bei getrennten Rechnern; Entfernen löscht den Eintrag, die Läufe bleiben. */
  canRemove: boolean;
  /** `null`, wenn der Ersteller fehlt oder nicht mehr im Mandanten ist. */
  setUpBy: RunnerSetUpBy | null;
  /** Stand der gemeldeten Version gegen die aktuelle des Servers (NEX-1740). Fehlt es, gibt es keinen Hinweis. */
  updateState?: RunnerUpdateState;
  /** Aktuelle Runner-Version des Servers; `null` ohne bereitgestelltes Paket. */
  latestVersion?: string | null;
}

export type RunnerUpdateState = 'CURRENT' | 'UPDATING' | 'AVAILABLE';

export interface RunnerSetUpBy {
  id: string;
  displayName: string;
}

export interface ProjectRunner {
  id: string;
  name: string;
  online: boolean;
  lastSeenAt: string;
  assignedAt: string;
  global: boolean;
  /** Bei Global-Rechnern der Mandanten-Admin, der ihn angelegt hat. */
  setUpBy: RunnerSetUpBy | null;
  connected: boolean;
}

export interface ProjectRunners {
  /** Ob dieser Mensch im Projekt verbinden und trennen darf; die Regel liegt beim Server. */
  canManage: boolean;
  runners: ProjectRunner[];
}

/**
 * Der Auftrag, den ein Rechner gerade ausführt. Darf der Mensch das Projekt nicht lesen, ist
 * `foreign` gesetzt und die Projektfelder fehlen: Man sieht, dass der Rechner belegt ist, nicht womit.
 */
export interface RunnerCurrentJob {
  jobId: string;
  startedAt: string;
  cancelRequested: boolean;
  foreign: boolean;
  projectId?: string;
  testCaseId?: string;
  testCaseTitle?: string;
  testRunId?: string;
  testRunKey?: string;
}

/**
 * „Diesen Rechner verbinden" von der Seite des angemeldeten Menschen aus.
 *
 * Hier liegt nur die Bestätigung. Den Code erzeugt der Runner selbst, und sein Zugangstoken
 * holt er sich auch selbst ab — testOFFICE bekommt es nie zu sehen.
 */
@Injectable({ providedIn: 'root' })
export class RunnerPairingService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiBaseUrl;

  /** Der bestätigte Rechner wird dem Projekt zugeordnet, sobald er seinen Zugang abholt. */
  confirm(userCode: string, projectId: string): Observable<RunnerPairingConfirmation> {
    return this.http.post<RunnerPairingConfirmation>(`${this.baseUrl}/api/v1/runner/pairing/confirm`, {
      userCode,
      projectId,
    });
  }

  listProjectRunners(projectId: string): Observable<ProjectRunners> {
    return this.http.get<ProjectRunners>(`${this.baseUrl}/api/v1/projects/${projectId}/runner-assignments`);
  }

  /** Trennt nur die Zuordnung zum Projekt; der Zugang des Rechners bleibt. */
  detachFromProject(projectId: string, runnerId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/v1/projects/${projectId}/runner-assignments/${runnerId}`);
  }

  /** Die verbundenen Rechner des Mandanten, auf Wunsch auch die getrennten; mit Projekt nur die dort nutzbaren. */
  listRunners(includeDisconnected = false, projectId?: string): Observable<PairedRunner[]> {
    const params: Record<string, string> = includeDisconnected ? { includeDisconnected: 'true' } : {};
    if (projectId) {
      params['projectId'] = projectId;
    }
    return this.http.get<PairedRunner[]>(`${this.baseUrl}/api/v1/runners`, { params });
  }

  renameRunner(runnerId: string, name: string): Observable<PairedRunner> {
    return this.http.patch<PairedRunner>(`${this.baseUrl}/api/v1/runners/${runnerId}`, { name });
  }

  /** Nur für getrennte Rechner; der Server lehnt sonst mit 409 ab. */
  removeRunner(runnerId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/api/v1/runners/${runnerId}/remove`, null);
  }

  /**
   * Trennt einen Rechner: Sein Zugang wird widerrufen, er meldet sich danach nicht mehr an. Der
   * Eintrag bleibt — er traegt die Laufhistorie.
   */
  disconnect(runnerId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/v1/runners/${runnerId}`);
  }

  /** Ersetzt die gepflegten Etiketten des Rechners durch `labels`; die gemeldeten bleiben. */
  updateLabels(runnerId: string, labels: string[]): Observable<PairedRunner> {
    return this.http.put<PairedRunner>(`${this.baseUrl}/api/v1/runners/${runnerId}/labels`, { labels });
  }

  /** Plattform-Kennungen der Pakete, die auf diesem Server bereitliegen. */
  listPackages(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/api/v1/runner-packages`);
  }

  /**
   * Das Paket kommt als Blob und nicht über einen einfachen Verweis: Der Endpunkt verlangt eine
   * Anmeldung, und ein `href` trüge den Anmeldekopf nicht mit.
   */
  downloadPackage(platform: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/api/v1/runner-packages/${platform}`, {
      responseType: 'blob',
    });
  }
}
